"use client";

import {
  BadgeCheck,
  Banknote,
  Building2,
  CalendarCheck,
  Ticket,
  Circle,
  DoorOpen,
  FileText,
  ArrowLeft,
  CreditCard,
  Gift,
  GraduationCap,
  HelpCircle,
  Layers,
  LayoutDashboard,
  LogOut,
  Rocket,
  Settings,
  TicketPercent,
  TrendingUp,
  UserCog,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/lib/contexts/auth-context";
import { useStudioContext } from "@/lib/contexts/studio-context";
import { usePermission } from "@/lib/hooks/use-permission";
import { supabase } from "@/lib/supabase";
import { fireCelebrationConfetti } from "@/lib/utils/confetti";
import type { Studio } from "@/types/database";
import { StudioPermission } from "@dnceclub/shared";
import toast from "react-hot-toast";
import { PreviewLinkBanner } from "./preview-link-banner";

interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  anyPermission?: StudioPermission[];
  ownerOnly?: boolean;
}

const topItems: NavItem[] = [
  { title: "Dashboard", url: "/owner/dashboard", icon: LayoutDashboard },
  { title: "Rentals", url: "/owner/bookings", icon: CalendarCheck, anyPermission: [StudioPermission.BOOKINGS_VIEW] },
  { title: "Classes", url: "/owner/classes", icon: GraduationCap, anyPermission: [StudioPermission.CLASSES_VIEW] },
  { title: "Events", url: "/owner/events", icon: Ticket, anyPermission: [StudioPermission.EVENTS_VIEW] },
  { title: "Payouts", url: "/owner/payouts", icon: Banknote, anyPermission: [StudioPermission.PAYOUTS_VIEW] },
];

const benefitsItems: NavItem[] = [
  { title: "Discounts", url: "/owner/discounts", icon: TicketPercent, anyPermission: [StudioPermission.DISCOUNTS_VIEW] },
  { title: "Rewards", url: "/owner/rewards", icon: Gift, anyPermission: [StudioPermission.REWARDS_VIEW] },
  { title: "Memberships", url: "/owner/memberships", icon: BadgeCheck, anyPermission: [StudioPermission.MEMBERSHIPS_VIEW] },
  { title: "Class Packs", url: "/owner/class-packs", icon: Layers, anyPermission: [StudioPermission.CLASS_PACKS_VIEW] },
  { title: "Gift Cards", url: "/owner/gift-cards", icon: CreditCard },
];

const manageItems: NavItem[] = [
  { title: "Analytics", url: "/owner/analytics", icon: TrendingUp, anyPermission: [StudioPermission.ANALYTICS_VIEW] },
  { title: "Customers", url: "/owner/customers", icon: Users, anyPermission: [StudioPermission.CUSTOMERS_VIEW] },
  { title: "Invoices", url: "/owner/invoices", icon: FileText, anyPermission: [StudioPermission.INVOICES_VIEW] },
  { title: "Instructors", url: "/owner/instructors", icon: Users, anyPermission: [StudioPermission.INSTRUCTORS_VIEW] },
  { title: "Staff", url: "/owner/staff", icon: UserCog, ownerOnly: true },
  { title: "Rooms", url: "/owner/rooms", icon: DoorOpen },
  { title: "Settings", url: "/owner/settings", icon: Settings, anyPermission: [StudioPermission.SETTINGS_VIEW] },
];

const setupItem: NavItem = {
  title: "Setup",
  url: "/owner/setup",
  icon: Rocket,
  ownerOnly: true,
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const { currentStudioId } = useStudioContext();
  const { hasAnyPermission, isOwner } = usePermission();
  const [studio, setStudio] = useState<Studio | null>(null);
  const [isLive, setIsLive] = useState(false);
  const [updatingLiveStatus, setUpdatingLiveStatus] = useState(false);
  const isActive = (url: string) =>
    location.pathname === url || location.pathname.startsWith(url + "/");

  const filterNavItems = (items: NavItem[]) =>
    items.filter((item) => {
      if (item.ownerOnly && !isOwner) return false;
      if (item.anyPermission) {
        return isOwner || hasAnyPermission(item.anyPermission);
      }
      return true;
    });

  const filteredTopItems = filterNavItems(topItems);
  const filteredBenefitsItems = filterNavItems(benefitsItems);
  const filteredManageItems = filterNavItems(manageItems);

  useEffect(() => {
    if (currentStudioId) {
      loadStudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStudioId]);

  // Listen for studio mutations from other surfaces (e.g. the Setup page's
  // Go Live confirm) so the sidebar's live indicator + preview banner
  // refresh without waiting for a full remount.
  useEffect(() => {
    if (!currentStudioId) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ studioId?: string }>).detail;
      if (!detail?.studioId || detail.studioId === currentStudioId) {
        void loadStudio();
      }
    };
    window.addEventListener("studio:changed", handler);
    return () => window.removeEventListener("studio:changed", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStudioId]);

  const hasActiveSubscription =
    studio?.subscription_status === "active" ||
    studio?.subscription_status === "trialing";

  const handleLiveToggle = async (checked: boolean) => {
    if (!studio?.id) return;

    // Block attempts to flip live on without an active subscription — the
    // backend enforces this too (POST /go-live returns SUBSCRIPTION_REQUIRED),
    // but we redirect to the subscription page instead of surfacing a raw
    // error when the sidebar toggle is the entry point.
    if (checked && !hasActiveSubscription) {
      toast.error("Start your subscription to go live.");
      navigate("/owner/settings?tab=billing");
      return;
    }

    setUpdatingLiveStatus(true);
    try {
      const { error } = await supabase
        .from("studios")
        .update({ is_live: checked })
        .eq("id", studio.id);

      if (error) throw error;

      setIsLive(checked);
      toast.success(checked ? "Studio is now live!" : "Studio is now offline");
      if (checked) fireCelebrationConfetti();
    } catch (error) {
      console.error("Error updating live status:", error);
      toast.error("Failed to update studio status");
    } finally {
      setUpdatingLiveStatus(false);
    }
  };

  const loadStudio = async () => {
    if (!currentStudioId) return;
    try {
      const { data: studioData, error: studioError } = await supabase
        .from("studios")
        .select("*")
        .eq("id", currentStudioId)
        .single();

      if (studioError) {
        setStudio(null);
        return;
      }

      if (studioData) {
        setStudio(studioData);
        setIsLive(studioData.is_live || false);
      }
    } catch (error) {
      console.error("Error loading studio:", error);
    }
  };

  return (
    <TooltipProvider>
      <Sidebar {...props}>
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex items-center gap-2 px-2 py-2">
                <Avatar className="h-8 w-8 shrink-0">
                  {studio?.logo_url && <AvatarImage src={studio.logo_url} alt={studio.name || "Studio logo"} />}
                  <AvatarFallback className="bg-on-dark text-on-light">
                    <Building2 className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 min-w-0 font-bold font-benzin text-sm leading-tight line-clamp-2">
                  {studio?.name || ""}
                </span>
                {isLive && (
                  <Circle className="h-2 w-2 shrink-0 fill-green-500 text-green-500 animate-pulse" />
                )}
                {isOwner && studio && (
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <div className="shrink-0">
                        <Switch
                          checked={isLive}
                          onCheckedChange={handleLiveToggle}
                          disabled={
                            updatingLiveStatus ||
                            !studio.is_launch_ready ||
                            (!hasActiveSubscription && !isLive)
                          }
                          className="data-[state=checked]:bg-green-500 data-[state=checked]:[&>span]:bg-white"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {!studio.is_launch_ready ? (
                        <p>Complete studio setup to go live</p>
                      ) : !hasActiveSubscription && !isLive ? (
                        <p>Start your subscription to go live</p>
                      ) : isLive ? (
                        <p>Studio is live and accepting rentals</p>
                      ) : (
                        <p>Studio is offline - rentals are disabled</p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
          {studio && !isLive && (
            <PreviewLinkBanner
              studioId={studio.id}
              studioHandle={studio.handle}
              previewToken={studio.preview_token}
              isLive={isLive}
            />
          )}
        </SidebarHeader>
        <SidebarContent>
          {/* Top section (no label — frequently visited) */}
          <SidebarGroup>
            <SidebarMenu>
              {isOwner && !studio?.is_live && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive(setupItem.url)}>
                    <Link to={setupItem.url}>
                      <setupItem.icon />
                      <span>{setupItem.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {filteredTopItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    className="h-9 group/menuitem"
                  >
                    <Link to={item.url}>
                      {item.icon && <item.icon className="h-4 w-4 !text-on-dark-muted group-hover/menuitem:!text-on-light-muted group-data-[active=true]/menuitem:!text-on-light-muted" />}
                      <span className="font-favorit-mono text-[13px] tracking-[0.01em]">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>

          {/* BENEFITS section */}
          {filteredBenefitsItems.length > 0 && (
            <SidebarGroup className="px-2 py-1">
              <SidebarGroupLabel className="font-favorit-mono text-[11px] uppercase tracking-wider text-on-dark-muted px-2">
                Benefits
              </SidebarGroupLabel>
              <SidebarMenu>
                {filteredBenefitsItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      className="h-9 group/menuitem"
                    >
                      <Link to={item.url}>
                        {item.icon && <item.icon className="h-4 w-4 !text-on-dark-muted group-hover/menuitem:!text-on-light-muted group-data-[active=true]/menuitem:!text-on-light-muted" />}
                        <span className="font-favorit-mono text-[13px] tracking-[0.01em]">{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
          )}

          {/* MANAGE section */}
          {filteredManageItems.length > 0 && (
            <SidebarGroup className="px-2 py-1">
              <SidebarGroupLabel className="font-favorit-mono text-[11px] uppercase tracking-wider text-on-dark-muted px-2">
                Manage
              </SidebarGroupLabel>
              <SidebarMenu>
                {filteredManageItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      className="h-9 group/menuitem"
                    >
                      <Link to={item.url}>
                        {item.icon && <item.icon className="h-4 w-4 !text-on-dark-muted group-hover/menuitem:!text-on-light-muted group-data-[active=true]/menuitem:!text-on-light-muted" />}
                        <span className="font-favorit-mono text-[13px] tracking-[0.01em]">{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
          )}

        </SidebarContent>
        <SidebarFooter>
          <div className="flex flex-col gap-2 p-2">
            <Link
              to="/owner/help"
              className="group/help flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-sidebar-accent transition-all duration-150"
            >
              <HelpCircle className="h-4 w-4 text-on-dark-muted group-hover/help:text-on-light-muted transition-colors duration-150" />
              <span className="font-favorit-mono text-xs text-on-dark group-hover/help:text-on-light transition-colors duration-150">Help Center</span>
            </Link>
            <div className="flex items-center gap-2.5 px-2 py-1.5">
              <Avatar className="h-7 w-7 rounded-lg">
                <AvatarImage
                  src={profile?.profile_photo || user?.user_metadata?.avatar_url}
                  alt={user?.email}
                />
                <AvatarFallback className="rounded-lg text-on-dark font-favorit-mono text-xs">
                  {user?.email?.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate font-favorit-mono text-xs font-medium text-on-dark">
                  {user?.email}
                </span>
                <span className="truncate font-favorit-mono text-[11px] text-on-dark-muted">
                  {isOwner ? "Owner" : "Staff"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate("/studios")}
                    className="h-8 w-8 shrink-0"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Back to renter view</p>
                </TooltipContent>
              </Tooltip>
              <Button
                variant="outline"
                onClick={signOut}
                className="flex-1 justify-start h-8 font-favorit-mono text-xs tracking-wide"
              >
                <LogOut className="mr-2 h-3.5 w-3.5" />
                SIGN OUT
              </Button>
            </div>
          </div>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
    </TooltipProvider>
  );
}
